import { apiRequest } from "./client";

/** Admin oversight of API tokens (backend/api/admin/api_tokens.py). */

export interface AdminApiToken {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  status: "active" | "revoked" | "expired";
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  owner_id: string;
  owner_name: string | null;
  owner_email: string | null;
  requests_total: number;
  requests_7d: number;
  changes_total: number;
  last_client: string | null;
  clients: string[];
}

export interface WidgetRef {
  id: string;
  page: string;
  label: string;
  what?: string[];
}

export interface ActivityDetails {
  fromRevision?: number;
  toRevision?: number;
  version?: number;
  counts?: { added: number; removed: number; changed: number };
  widgetsAdded?: WidgetRef[];
  widgetsRemoved?: WidgetRef[];
  widgetsChanged?: WidgetRef[];
  pagesAdded?: string[];
  pagesRemoved?: string[];
  pagesRenamed?: { from: string; to: string }[];
  reportSettingsChanged?: string[];
  errors?: string[];
  measures?: string[];
  dimensions?: string[];
  [key: string]: unknown;
}

export interface ActivityItem {
  id: string;
  created_at: string;
  token_id: string;
  token_name: string | null;
  token_prefix: string | null;
  owner_id: string;
  owner_name: string | null;
  owner_email: string | null;
  client: string;
  method: string;
  path: string;
  action: string;
  action_label: string;
  is_change: boolean;
  status_code: number;
  duration_ms: number;
  target_type: string | null;
  target_id: string | null;
  target_name: string | null;
  summary: string;
  details: ActivityDetails | null;
  ip_address: string | null;
  user_agent: string | null;
}

export interface ActivityPage {
  items: ActivityItem[];
  next_before: string | null;
}

export interface ApiTokenSummary {
  active_tokens: number;
  people_with_tokens: number;
  people_active_7d: number;
  requests_7d: number;
  changes_7d: number;
  failed_7d: number;
  clients_7d: { client: string; requests: number }[];
}

export interface LifecycleEvent {
  created_at: string;
  event: string;
  actor_name: string | null;
  context: Record<string, unknown> | null;
}

export interface ActivityFilters {
  token_id?: string;
  user_id?: string;
  client?: string;
  changes_only?: boolean;
  failed_only?: boolean;
  before?: string;
  limit?: number;
}

const BASE = "/api/v1/admin/api-tokens";

export const apiTokensAdminApi = {
  list: () => apiRequest<AdminApiToken[]>(BASE),
  summary: () => apiRequest<ApiTokenSummary>(`${BASE}/summary`),
  activity: (f: ActivityFilters) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== "" && v !== false) q.set(k, String(v));
    return apiRequest<ActivityPage>(`${BASE}/activity${q.toString() ? `?${q}` : ""}`);
  },
  lifecycle: (id: string) => apiRequest<LifecycleEvent[]>(`${BASE}/${id}/lifecycle`),
  revoke: (id: string) => apiRequest<AdminApiToken>(`${BASE}/${id}/revoke`, { method: "POST" }),
};
